import { useId, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDownIcon, CornerDownRightIcon } from 'lucide-react';

import type { TaskView } from '@/lib/api/generated/model';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/shared/components/PageHeader';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';
import { formatMemory } from '@/features/organization-admin';
import { formatCost, runDuration } from '@/features/tasks';

import type { RunSummary } from '../types';
import { RunStatusChip } from './RunStatusChip';

/**
 * A model id without its trailing release date — `claude-haiku-4-5-20251001`
 * reads as `claude-haiku-4-5`. The full id stays on the element's `title`.
 */
function shortModel(model: string): string {
  return model.replace(/-\d{8}$/, '');
}

/**
 * The words the service used to write over the fan-out's own report when the
 * join closed ("2 of 2 created tasks finished."). It no longer does, but runs
 * closed before that still carry them, and on this page they only repeat the
 * progress bar — so they are not shown as a report.
 */
const JOIN_SUMMARY = /^\d+ of \d+ created tasks finished\.$/;

/**
 * A step prompt as written, without the indentation a YAML block scalar
 * leaves on its continuation lines — "  describing what…" read as a stray
 * indent rather than as part of the sentence.
 */
function unindent(text: string): string {
  return text.replace(/\n[ \t]+/g, '\n');
}

export interface RunHeaderProps {
  workspaceId: string;
  /** The parent task's own workflow key — where its board, and its drawer, live. */
  workflowKey: string;
  parentWorkflowName: string;
  summary: RunSummary;
  /** The parent task itself, for its description and who created it when. */
  parent: Pick<TaskView, 'description' | 'created_at' | 'created_by'>;
}

/**
 * The run's title and status, then the parent task it belongs to — laid out
 * as the demo's task page lays out its own head (`docs/demos/workflows`):
 * "Current step" beside "Details".
 *
 * - The title is the parent and the workflow it ran — "admin page → Double" —
 *   because the workflow's name alone is the same on every run a parent has.
 *   The status sits beside it, open or closed.
 * - **Current step** says what the parent is doing about this run: waiting for
 *   how many tasks, and where it goes once they are done — or, once closed,
 *   where it went. Under it, what the fan-out reported doing ("split into a
 *   backend and a frontend task"), and, folded away as troubleshooting, the
 *   step's prompt (its declared text as the workflow stands now — the caveat
 *   `BoardStepSection.prompt` documents) and the run's model, cost and time.
 * - **Details** is the parent itself: its workflow, who created it when, and
 *   its description in full — the goal these tasks serve, which is read, not
 *   hunted for.
 */
export function RunHeader({
  workspaceId,
  workflowKey,
  parentWorkflowName,
  summary,
  parent,
}: RunHeaderProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsId = useId();
  const {
    parentTask,
    targetWorkflowName,
    stepName,
    stepPrompt,
    nextStep,
    closed,
    parentStepNow,
    execution,
    done,
    total,
  } = summary;
  const description = parent.description?.trim();
  const rawReport = execution.result_summary?.trim();
  const report =
    rawReport && !JOIN_SUMMARY.test(rawReport) ? rawReport : undefined;
  const prompt = stepPrompt ? unindent(stepPrompt) : null;
  const duration = runDuration(execution);
  const stops = execution.memory_stops ?? [];

  // A figure the service did not record is left out, never shown as zero.
  const runFacts = [
    execution.model_snapshot ? (
      <span key="model" title={execution.model_snapshot}>
        {shortModel(execution.model_snapshot)}
      </span>
    ) : null,
    execution.cost != null ? (
      <span key="cost" className="tabular-nums">
        {formatCost(execution.cost, execution.currency)}
      </span>
    ) : null,
    duration ? <span key="took">took {duration}</span> : null,
    // Compared to null, never truthiness: 0 is a recorded figure. The snapshot carries no
    // unit, so MiB is stated rather than invented.
    execution.memory_mib != null ? (
      <span key="memory" className="tabular-nums">
        {formatMemory(execution.memory_mib, 'MiB')} memory
      </span>
    ) : null,
    // Three states, never collapsed: false warns, null is silence, true has no fact.
    execution.memory_enforced === false ? (
      <span key="not-enforced" className="text-destructive font-medium">
        The memory budget was not enforced for this run
      </span>
    ) : null,
    // A budget beside a run that was not enforced would claim a bound that did not hold.
    execution.memory_enforced === true &&
    execution.memory_budget_mib != null ? (
      <span key="budget" className="tabular-nums">
        commands held to {formatMemory(execution.memory_budget_mib, 'MiB')}
      </span>
    ) : null,
  ].filter(Boolean);
  const hasDetails = Boolean(prompt) || runFacts.length > 0 || stops.length > 0;
  const remaining = total - done;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        eyebrow="Sub-workflow run"
        title={
          <>
            <Link
              to="/workspaces/$workspaceId/workflows/$workflowKey/board"
              params={{ workspaceId, workflowKey }}
              search={{ task: parentTask.id }}
              className="underline-offset-4 hover:underline"
            >
              {parentTask.title}
            </Link>
            <span aria-hidden="true" className="text-muted-foreground">
              {' → '}
            </span>
            <span className="sr-only"> ran </span>
            {targetWorkflowName}
          </>
        }
        aside={<RunStatusChip summary={summary} />}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card size="sm" role="region" aria-label="Current step">
          <CardHeader>
            <CardTitle>Current step</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-muted-foreground">
              {closed ? (
                <>
                  Ran <b className="text-foreground font-medium">{stepName}</b>.{' '}
                  {parentStepNow ? (
                    <>
                      The task is now at{' '}
                      <b className="text-foreground font-medium">
                        {parentStepNow}
                      </b>
                      .
                    </>
                  ) : (
                    'The task has moved on.'
                  )}
                </>
              ) : (
                <>
                  <b className="text-foreground font-medium">{stepName}</b> is
                  waiting for {remaining} of {total}{' '}
                  {total === 1 ? 'task' : 'tasks'} on {targetWorkflowName}.
                  {nextStep ? (
                    <>
                      {' '}
                      The task continues to{' '}
                      <b className="text-foreground font-medium">
                        {nextStep.name}
                      </b>{' '}
                      when all are done.
                    </>
                  ) : null}
                </>
              )}
            </p>

            {report ? (
              <p className="text-foreground flex gap-1.5">
                <CornerDownRightIcon
                  aria-hidden="true"
                  className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
                  strokeWidth={1.75}
                />
                <span className="whitespace-pre-wrap">
                  <span className="sr-only">The run reported: </span>
                  {report}
                </span>
              </p>
            ) : null}

            {hasDetails ? (
              <button
                type="button"
                aria-expanded={detailsOpen}
                aria-controls={detailsId}
                onClick={() => {
                  setDetailsOpen((was) => !was);
                }}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex w-fit items-center gap-1 rounded-sm text-xs underline underline-offset-4 focus-visible:ring-3 focus-visible:outline-none"
              >
                Step prompt & run details
                <ChevronDownIcon
                  aria-hidden="true"
                  className={`size-3.5 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
                  strokeWidth={1.75}
                />
              </button>
            ) : null}

            {detailsOpen ? (
              <dl
                id={detailsId}
                className="border-border grid gap-x-6 gap-y-3 border-l pl-4 sm:grid-cols-[auto_1fr]"
              >
                {prompt ? (
                  <>
                    <dt className="text-muted-foreground">Step prompt</dt>
                    <dd className="text-foreground whitespace-pre-wrap">
                      {prompt}
                    </dd>
                  </>
                ) : null}
                {runFacts.length > 0 ? (
                  <>
                    <dt className="text-muted-foreground">Run</dt>
                    <dd className="text-muted-foreground flex flex-wrap gap-x-2 text-xs leading-5">
                      {runFacts.flatMap((item, index) =>
                        index === 0
                          ? [item]
                          : [
                              <span
                                key={`sep-${String(index)}`}
                                aria-hidden="true"
                              >
                                ·
                              </span>,
                              item,
                            ],
                      )}
                    </dd>
                  </>
                ) : null}
                {stops.length > 0 ? (
                  <>
                    <dt className="text-muted-foreground">Memory stops</dt>
                    <dd className="flex flex-col gap-1.5 text-xs leading-5">
                      {stops.map((stop, index) => (
                        <p
                          key={`${String(index)}-${stop.stopped_at}`}
                          className="text-foreground"
                        >
                          <code
                            title={stop.command}
                            className="bg-muted inline-block max-w-full truncate rounded px-1 align-bottom font-mono"
                          >
                            {stop.command}
                          </code>{' '}
                          {stop.cause === 'budget'
                            ? `exceeded its budget of ${formatMemory(stop.budget_mib, 'MiB')}, at a sampled peak of ${formatMemory(stop.peak_mib, 'MiB')}.`
                            : `was stopped because the pod ran out of memory across commands running together${
                                execution.memory_mib != null
                                  ? ` (${formatMemory(execution.memory_mib, 'MiB')})`
                                  : ''
                              }; the highest sampled peak was ${formatMemory(stop.peak_mib, 'MiB')}.`}
                        </p>
                      ))}
                    </dd>
                  </>
                ) : null}
              </dl>
            ) : null}
          </CardContent>
        </Card>

        <Card size="sm" role="region" aria-label="Parent task details">
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
              <dt className="text-muted-foreground">Workflow</dt>
              <dd>
                <Link
                  to="/workspaces/$workspaceId/workflows/$workflowKey/board"
                  params={{ workspaceId, workflowKey }}
                  className="text-foreground underline-offset-4 hover:underline"
                >
                  {parentWorkflowName}
                </Link>
              </dd>

              <dt className="text-muted-foreground">Created</dt>
              <dd
                className="text-foreground"
                title={formatExactTime(parent.created_at)}
              >
                {formatRelativeTime(parent.created_at)}
                {parent.created_by
                  ? ` by ${parent.created_by.display_name}`
                  : null}
              </dd>

              <dt className="text-muted-foreground">Description</dt>
              <dd className="text-foreground whitespace-pre-wrap">
                {description ?? (
                  <span className="text-muted-foreground italic">
                    No description.
                  </span>
                )}
              </dd>
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
